import { useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import styled from "styled-components";

import type { IconName } from "@similarweb/icons";
import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { BannerContainer, BannerPrimaryText, IconButton } from "@similarweb/ui-components";

import { TeaserBanner } from "app/components/banner/TeaserBanner";
import { translate } from "app/filters/i18nFilter";
import { getDefaultParamsForState } from "app/pages/digital-suite/home-page/helpers";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { allTrackers } from "app/services/track/track";

export const NewPageNotificationBar = styled.div`
    margin-bottom: 24px;
    ${BannerPrimaryText} {
        ${setFontVariant("subtitle1")}
    }
    ${BannerContainer} {
        background: ${colorsPalettes.blue[100]};
        padding: 16px;
    }
`;

const ButtonForBanner = styled(IconButton)`
    flex-direction: row-reverse;
    ${SWReactIcons} {
        margin-left: 6px;
        margin-right: 0;
    }
`;

const WEBSITE_KEYWORDS_PAGE_NAME = "Website Keywords";
const ORGANIC_LANDING_PAGES_PAGE_NAME = "Landing Pages";
const KEYWORD_ANALYSIS_PAGE_NAME = "Keyword Analysis";
interface INotificationBannerProps {
    pageName: string;
    bannerStorageName?: string;
    titleTxt?: string;
    subTitleTxt?: string;
    buttonTxt?: string;
    iconName?: IconName;
    stateName?: string;
    displayCloseBtn?: boolean;
    overrideParams?: Record<string, string>;
}
export const NotificationBannerForNewPages = (props: INotificationBannerProps) => {
    const {
        bannerStorageName,
        titleTxt = translate("webanalysis.search.new.page.banner.title.general"),
        subTitleTxt,
        buttonTxt,
        iconName = "megaphone",
        pageName,
        displayCloseBtn,
    } = props;
    const BANNER_STORAGE_NAME = bannerStorageName || "Search3.0NotificationBanner";
    const { params } = useCurrentStateAndParams();

    const [isVisible, setIsVisible] = useState(!sessionStorage.getItem(BANNER_STORAGE_NAME));

    const handleCloseClick = () => {
        allTrackers.dispatchTrackEvent("Button", "click", `Close Search 3.0 banner/${pageName || ""}`);

        sessionStorage.setItem(BANNER_STORAGE_NAME, "false");
        setIsVisible(false);
    };

    const getStateNameRedirect = (pageName) => {
        switch (pageName) {
            case WEBSITE_KEYWORDS_PAGE_NAME:
                return "organicsearch_websites_v2_traffic_keywords";
            case ORGANIC_LANDING_PAGES_PAGE_NAME:
                return "organicsearch_website_landingpages_v2";
            case KEYWORD_ANALYSIS_PAGE_NAME:
                return "organicsearch_keywordAnalysis_2";
            default:
                return "organicsearch_websites_v2_traffic_keywords";
        }
    };
    const handleButtonClick = () => {
        setIsVisible(false);
        sessionStorage.setItem(BANNER_STORAGE_NAME, "false");
        allTrackers.dispatchTrackEvent("Button", "click", `Search 3.0 banner/${pageName || ""}`);
        const stateName = getStateNameRedirect(pageName);
        proNavigator.go(stateName, {
            ...getDefaultParamsForState(stateName),
            key: params.key,
            ...(props.overrideParams ?? {}),
        });
    };
    const button = (
        <ButtonForBanner
            onClick={handleButtonClick}
            iconName="arrow-right"
            type="primary"
        >
            {buttonTxt}
        </ButtonForBanner>
    );

    return (
        isVisible && (
            <NewPageNotificationBar>
                {!displayCloseBtn ? (
                    <TeaserBanner
                        title={titleTxt}
                        subtitle={subTitleTxt}
                        CustomIcon={
                            <SWReactIcons
                                size="xl"
                                iconName={iconName}
                            />
                        }
                        customButton={buttonTxt ? button : null}
                    />
                ) : (
                    <TeaserBanner
                        title={titleTxt}
                        subtitle={subTitleTxt}
                        onCloseClick={handleCloseClick}
                        CustomIcon={
                            <SWReactIcons
                                size="xl"
                                iconName={iconName}
                            />
                        }
                    />
                )}
            </NewPageNotificationBar>
        )
    );
};
